<template>
  <v-card>
    <v-card-title>
      {{isEditMode ? $t(`variation.editFormTitle.${type}`) : $t(`variation.newFormTitle.${type}`)}}
    </v-card-title>
    <v-card-text>
      <v-form>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.no"
              :label="$t('variation.number')"
              dense
              outlined
              @input="$v.form.no.$touch()"
              :error-messages="errorMessages('no')"
            />
          </v-col>
          <v-col v-if="formFields.includes('stage')">
            <v-select
              v-model="form.stage"
              :items="stagesList"
              item-text="name"
              item-value="value"
              :label="$t('variation.stage')"
              dense
              outlined
              :disabled="isEditMode"
              @input="$v.form.stage.$touch()"
              :error-messages="errorMessages('stage')"
            />

          </v-col>
          <v-col>
            <v-select
              v-model="form.status"
              :items="statusesList"
              :label="$t('variation.status')"
              dense
              outlined
              :disabled="isFinalised"
              @input="$v.form.status.$touch()"
              :error-messages="errorMessages('status')"
            ></v-select>
          </v-col>
          <v-col>
            <v-text-field
              v-model="form.referenceNumber"
              :label="$t('variation.reference')"
              dense
              outlined
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="form.name"
              :label="$t('variation.title')"
              counter="64"
              dense
              outlined
              @input="$v.form.name.$touch()"
              :error-messages="errorMessages('name')"
            />
          </v-col>
          <v-col>
            <vf-date-input
              v-model="form.receivedDate"
              :label="$t(`variation.receivedDate.${this.reporter}`)"
              outlined
              dense
              @change="$v.form.receivedDate.$touch()"
              :error-messages="errorMessages('receivedDate')"
            />
          </v-col>
          <v-col>
            <v-combobox
              v-model="form.risk"
              :items="risksList"
              :search-input.sync="riskSearch"
              item-text="name"
              item-value="id"
              return-object
              :label="$tc('dictionaries.risk', 1)"
              dense
              outlined
            >
              <template v-slot:item="{ item }">
                <v-list-item-content :key="item.id">
                  <v-list-item-title>{{item.name}}</v-list-item-title>
                  <v-list-item-subtitle>{{item.description}}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:no-data>
                <v-row class="ma-1">
                  <v-col>{{ $t('dictionaries.noData') }}</v-col>
                </v-row>
                <v-row class="ma-1" justify="center">
                  <v-btn
                    color="primary"
                    dark
                    rounded
                    small
                    outlined
                    @click="handleOpenRiskDialog"
                  >
                    {{ $t('dictionaries.addNew.risk') }}
                  </v-btn>
                </v-row>
              </template>
            </v-combobox>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-if="area.name === 'onshore' && formFields.includes('workstream')"
            cols="4"
          >
            <v-combobox
              v-model="form.workstream"
              :items="workstreamsList"
              item-text="name"
              item-value="id"
              :label="$t('variation.workstream')"
              dense
              outlined
            />
          </v-col>
          <v-col v-if="formFields.includes('category')">
            <v-combobox
              v-model="form.category"
              :items="categoriesList"
              :label="$t('variation.category')"
              outlined
              dense
              item-text="name"
              item-value="value"
            />
          </v-col>
          <v-col>
            <PartsSelect
              v-model="form.partsIds"
              :parts="partsList"
              :isMultiCurrency="isMultiCurrency"
              @input="handleParts"
              :validation-model="$v.form.parts"
              :error-messages="errorMessages('parts')"
              multiple
              @select-all="handleParts(partsList.map(i => i.id))"
              @unselect-all="handleParts([])"
              :disabled="isFinalised"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              v-model="form.description"
              :label="$t('variation.notes')"
              dense
              outlined
              counter="256"
              rows="2"
              @input="$v.form.description.$touch()"
              :error-messages="errorMessages('description')"
            />
          </v-col>
        </v-row>
        <div class="mx-2">
          <v-row v-if="form.parts.length > 1" class="pa-0 ma-0" align="center">
            <v-checkbox
              v-model="useValuesForParts"
              :label="$t('variation.useValuesMessage')"
              dense
              hide-details
              class="pb-2"
              @change="v => showPartsDetails = v ? false : showPartsDetails"
              :disabled="(isMultiCurrencyVariation && useValuesForParts) || isFinalised"
            />
            <v-spacer />
            <v-btn
              @click="showPartsDetails = !showPartsDetails"
              v-if="form.parts.length > 1 && !useValuesForParts"
              text
              small
              rounded
              :class="percentSumError ? 'orange--text' : 'text--secondary'"
            >
              {{ $t('variation.showSiteDetails') }}
              <v-icon>{{ showPartsDetails ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
            </v-btn>
          </v-row>
          <v-row v-if="percentSumError" class="mt-0">
            <v-spacer />
            <span class="orange--text">{{ $t('variation.partsPercentIncorrect') }}</span>
          </v-row>
          <template v-if="useValuesForParts">
            <v-row
              v-for="part in form.parts"
              :key="part.partId"
            >
              <ValuesForm
                :form="part"
                :currency="part.currency"
                :is-finalised="isFinalised"
                :reporter="reporter"
                :error-messages="errorMessages"
                :validation-model="$v"
                :type="type"
                :label="$t('variation.partDescription', part)"
                @change="calculatePartsPrices"
                @dirty="handleSetDirty"
              />
            </v-row>
          </template>
          <template v-else>
            <ValuesForm
              :form="form"
              :currency="currency"
              :is-finalised="isFinalised"
              :reporter="reporter"
              :error-messages="errorMessages"
              :validation-model="$v"
              :originalValuesError="originalValuesError"
              :agreedValuesError="agreedValuesError"
              :type="type"
              @change="calculatePartsPrices"
              @dirty="handleSetDirty"
            />
            <RationsForParts
              v-if="form.parts.length > 1 && showPartsDetails"
              :form="form"
              :reporter="reporter"
              @calculate="calculatePartsPrices"
              @dirty="handleSetDirty"
              :type="type"
            />
          </template>
        </div>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn
        class="mr-2"
        v-if="formFields.includes('unlock') && isFinalised"
        rounded
        :dark="!dirty"
        dense
        small
        color="warning"
        @click="handleUnlockVariation"
        :disabled="dirty"
      >
        <v-icon small>mdi-lock-open-outline</v-icon>
        {{ $t('common.unlock') }}
      </v-btn>
      <v-btn
        class="mr-2"
        v-if="(formFields.includes('finalize') || form.type === 'claim') && form.id && form.stage === 2 && !isFinalised"
        rounded
        :dark="!dirty"
        dense
        small
        color="warning"
        @click="handleFinalizeVariation"
        :disabled="dirty"
      >
        <v-icon small>mdi-lock-outline</v-icon>
        {{ $t('common.finalize') }}
      </v-btn>
      <v-btn
        class="mr-2"
        v-if="showToVariationButton"
        rounded
        :dark="!dirty"
        dense
        outlined
        small
        color="success"
        @click="handleChangeTo('variation')"
        :disabled="dirty"
      >
        {{ $t('variation.toVariation') }}
      </v-btn>
      <v-btn
        class="mr-2"
        rounded
        :dark="!dirty"
        dense
        small
        :color="form.stage === 1 ? 'primary' : 'orange'"
        @click="handleUpgradeClick"
        v-if="formFields.includes('stageChange') && form.stage < 2 && isEditMode"
        :disabled="dirty"
      >
        {{ $t('variation.changeTo', { new: stagesList[form.stage + 1].name }) }}
      </v-btn>
      <v-btn
        class="mr-2"
        v-if="isEditMode && formFields.includes('toClaim')"
        rounded
        dense
        small
        outlined
        color="warning"
        @click="handleChangeTo('claim')"
        :disabled="dirty"
      >
        {{ $t('variation.toClaim') }}
      </v-btn>
      <v-btn
        class="mr-2"
        v-if="formFields.includes('milestoneButton') && isEditMode && value.agreedValueCents"
        rounded
        dense
        small
        outlined
        color="warning"
        @click="handleCreateOrUpdateMilestone"
        :disabled="dirty || isFinalised"
      >
        {{ $t(value.paymentMilestone ? 'variation.updateMilestone' : 'variation.createMilestone') }}
      </v-btn>
      <v-spacer />
      <v-btn
        rounded
        dense
        outlined
        small
        color="primary"
        @click="handleCancelClick"
      >
        {{ $t('common.cancel') }}
      </v-btn>
      <SubmitButton small @click="handleSaveClick" :disabled="percentSumError" />
    </v-card-actions>
  </v-card>
</template>

<script>
import dayjs from 'dayjs'
import { DATE_FORMAT, DEFAULT_VARIATION } from '@/helpers'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { errorMessageGenerator, variationModel } from '@/validators'
import { mapState } from 'vuex'
import {
  saveCause,
  getAllDictionaries,
  updateVariation,
  createVariationForContract,
  createMilestoneForVariation
} from '@/services'

export default {
  name: 'VariationForm',
  props: {
    value: {
      type: Object,
      default: () => DEFAULT_VARIATION[this.type]
    },
    area: Object,
    type: String,
    reporter: String,
    parts: {
      type: Array,
      default: () => []
    },
    formFields: {
      type: Array,
      default: () => []
    },
    isMultiCurrency: Boolean,
    baseCurrency: Object,
    currencies: Array
  },
  data () {
    const risk = this.value ? this.$store.state.risks.find(i => i.id === this.value.riskId) : null
    const status = this.$store.state.statuses.find((i) => i.code === this.value.status)
    const valueIsMultiCurrency = this.value.id ? [...new Set(this.value.parts.map(i => i.currency))].length > 1 : this.isMultiCurrency
    return {
      form: this.prepareVariation(this.value),
      receivedDateMenu: false,
      agreedValuesError: [],
      originalValuesError: [],
      riskSearch: risk ? risk.name : '',
      isFinalised: status ? !status.editable.includes(this.type) : false,
      useValuesForParts: this.isMultiCurrency && valueIsMultiCurrency,
      showPartsDetails: false,
      dirty: false,
      changeFieldsModalError: null
    }
  },
  created () {
    this.$eventBus.$on('CLICK_OUTSIDE_MODAL', () => {
      this.cleanAndCloseForm()
    })
  },
  computed: {
    ...mapState({
      variationsStages: (state) => state.variationsStages,
      statuses: (state) => state.statuses,
      categories: (state) => state.categories,
      workstreams: (state) => state.workstreams,
      areas: (state) => state.areas,
      risksList: state => state.risks
    }),
    percentSum () {
      return this.form.parts.reduce((acc, curr) => acc + Number(curr.variationRatio), 0)
    },
    percentSumError () {
      return this.form.parts.length > 1 && !this.useValuesForParts && this.percentSum !== 100
    },
    partsCurrencies () {
      return [...new Set(this.form.parts.map(i => i.currency))]
    },
    currency () {
      return this.partsCurrencies[0] || this.baseCurrency.key
    },
    isMultiCurrencyVariation () {
      return [...new Set(this.form.parts.map(i => i.currency))].length > 1
    },
    showToVariationButton () {
      return this.formFields.includes('toVariation') && ((this.form.stage === 2 && this.form.type === 'claim' && this.isEditMode) || (this.isEditMode && this.type === 'early_warning'))
    },
    partsList () {
      return this.parts
    },
    stagesList () {
      return this.variationsStages[this.type].map((s, i) => ({
        name: s.name,
        value: i
      }))
    },
    categoriesList () {
      return this.categories.filter((i) => {
        const type = i.type.split('-')
        return (
          i.area.code === this.area.code &&
          type[0] === this.type &&
          (type[1] ? type[1] === this.reporter : true)
        )
      })
    },
    workstreamsList () {
      return this.workstreams.filter(
        (i) => i.area.code === this.area.code && i.type === this.type
      )
    },
    statusesList () {
      return this.statuses
        .filter((i) => i.show.includes(this.type))
        .map((i) => ({
          text: i.name,
          value: i.code,
          disabled: i.disabled.includes(this.type)
        }))
    },
    receivedDate: {
      get () {
        return this.form.receivedDate
          ? dayjs(this.form.receivedDate).format(DATE_FORMAT)
          : dayjs().format(DATE_FORMAT)
      },
      set (value) {
        this.form.receivedDate = dayjs(value).toDate()
      }
    },
    isEditMode () {
      return !!this.form.id
    },
    hasStatusChanged () {
      return this.isEditMode && this.form.status !== this.value.status
    },
    hasAnyChanges () {
      if (!this.isEditMode) {
        return true
      }

      return !isEqual(
        this.normalizeVariationForSaveCheck(this.form),
        this.normalizeVariationForSaveCheck(this.buildVariationForm(this.value))
      )
    },
    shouldRequireChangeDetails () {
      if (!this.isEditMode) {
        return false
      }

      return !isEqual(
        this.normalizeVariationForComparison(this.form),
        this.normalizeVariationForComparison(this.buildVariationForm(this.value))
      )
    }
  },
  watch: {
    value (v) {
      this.form = this.prepareVariation(v)
    },
    type () {
      this.form = this.prepareVariation(this.value)
    },
    reporter () {
      this.form = this.prepareVariation(this.value)
    },
    '$v.$anyDirty' (v, p) {
      if (v && !p && !this.dirty) {
        this.dirty = true
      }
    }
  },
  methods: {
    calculatePartsPrices () {
      if (!this.useValuesForParts) {
        this.form.parts.forEach(part => {
          part.estimatedValueCents = this.form.estimatedValueCents === null ? null : Math.round(this.form.estimatedValueCents * (part.variationRatio / 100))
          part.originalValueCents = this.form.originalValueCents === null ? null : Math.round(this.form.originalValueCents * (part.variationRatio / 100))
          part.agreedValueCents = this.form.agreedValueCents === null ? null : Math.round(this.form.agreedValueCents * (part.variationRatio / 100))
          part.eotAgreed = !this.form.eotAgreed ? null : Math.round(this.form.eotAgreed * (part.variationRatio / 100))
          part.eotRequested = !this.form.eotRequested ? null : Math.round(this.form.eotRequested * (part.variationRatio / 100))
        })
        const sum = this.form.parts.reduce((acc, curr) => {
          return {
            estimatedValueCents: acc.estimatedValueCents + curr.estimatedValueCents,
            originalValueCents: acc.originalValueCents + curr.originalValueCents,
            agreedValueCents: acc.agreedValueCents + curr.agreedValueCents,
            eotAgreed: acc.eotAgreed + curr.eotAgreed,
            eotRequested: acc.eotRequested + curr.eotRequested
          }
        }, { estimatedValueCents: 0, originalValueCents: 0, agreedValueCents: 0, eotAgreed: 0, eotRequested: 0 })
        const partsLength = this.form.parts.length - 1
        Object.keys(sum).forEach(key => {
          if (sum[key] > Number(this.form[key])) {
            this.form.parts[partsLength][key] += (Number(this.form[key]) - sum[key])
          }
        })
      }
    },
    handleParts (v) {
      this.form.parts = v.map(newPart => {
        const part = this.form.parts.find(i => i.partId === (newPart.id || newPart)) || this.parts.find(i => i.id === (newPart.id || newPart))
        return {
          partId: part.partId || part.id,
          currency: part.currency,
          ratio: part.ratio,
          name: part.name,
          variationRatio: part.ratio,
          estimatedValueCents: part.estimatedValueCents || null,
          originalValueCents: part.originalValueCents || null,
          agreedValueCents: part.agreedValueCents || null,
          eotRequested: part.eotRequested || null,
          eotAgreed: part.eotAgreed || null
        }
      })
      this.form.partsIds = v.map(i => i.id || i)
      if (this.form.partsIds.length === 1) {
        this.useValuesForParts = false
        this.form.parts[0].variationRatio = 100
      }
      if (this.partsCurrencies.length > 1) {
        this.useValuesForParts = true
      }
    },
    checkForAgreedValueError () {
      if (!this.form.agreedValueCents) {
        this.agreedValuesError.push(this.$t('variation.agreedValueErrorForMilestone'))
      }
    },
    getApiErrorResponse (error) {
      return error && (error.parsedResponse || error.errorResponse || null)
    },
    isChangeFieldsRequiredError (error) {
      const errorResponse = this.getApiErrorResponse(error)
      return errorResponse && errorResponse.statusCode === 400 && errorResponse.message === this.$t('variation.changeFieldsRequiredError')
    },
    isApplicationExistsError (error) {
      const errorResponse = this.getApiErrorResponse(error)
      return errorResponse && errorResponse.statusCode === 400 && errorResponse.message === 'APPLICATION_EXISTS'
    },
    getStatusName (statusCode) {
      const status = this.statuses.find(i => i.code === statusCode)
      return status ? status.name : statusCode
    },
    getTypeName (typeCode) {
      return this.$t(`variation.typeName.${typeCode}`)
    },
    clearChangeDetails () {
      this.form.changeDescription = ''
    },
    setAutoStatusChangeDetails (fromStatus, toStatus) {
      this.form.changeDescription = this.$t('variation.statusChangeDescription', {
        from: this.getStatusName(fromStatus),
        to: this.getStatusName(toStatus)
      })
    },
    setAutoTypeChangeDetails ({ fromType, toType, fromStatus, toStatus }) {
      this.form.changeDescription = this.$t('variation.typeChangeDescription', {
        fromType: this.getTypeName(fromType),
        toType: this.getTypeName(toType),
        fromStatus: this.getStatusName(fromStatus),
        toStatus: this.getStatusName(toStatus)
      })
    },
    normalizeVariationForComparison (value) {
      return {
        ...cloneDeep(value),
        category: value.category && value.category.name ? value.category.name : value.category,
        workstream: value.workstream && value.workstream.name ? value.workstream.name : value.workstream,
        risk: undefined,
        riskId: value.risk ? value.risk.id : value.riskId || null,
        status: undefined,
        changeDescription: undefined,
        lastUpdateDate: undefined,
        partsIds: undefined,
        isGhost: undefined,
        paymentMilestone: undefined
      }
    },
    normalizeVariationForSaveCheck (value) {
      return {
        ...cloneDeep(value),
        category: value.category && value.category.name ? value.category.name : value.category,
        workstream: value.workstream && value.workstream.name ? value.workstream.name : value.workstream,
        risk: undefined,
        riskId: value.risk ? value.risk.id : value.riskId || null,
        changeDescription: undefined,
        lastUpdateDate: undefined,
        partsIds: undefined,
        isGhost: undefined,
        paymentMilestone: undefined
      }
    },
    prepareUpdatePayload () {
      return {
        ...this.form,
        lastUpdateDate: undefined,
        partsIds: undefined,
        risk: undefined,
        isGhost: undefined,
        changeDescription: (this.form.changeDescription || '').trim()
      }
    },
    openChangeDetailsDialog () {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'VariationChangeDetailsForm',
        options: {
          maxWidth: 650,
          persistent: true
        },
        scope: {
          title: this.$t('variation.saveUpdateTitle'),
          value: {
            changeDescription: this.form.changeDescription
          },
          errorMessage: this.changeFieldsModalError
        },
        handlers: {
          input: this.handleChangeDetailsSave
        }
      })
    },
    async handleChangeDetailsSave ({ changeDescription }) {
      this.form.changeDescription = changeDescription
      this.changeFieldsModalError = null

      try {
        await this.saveForm()
        this.$eventBus.$emit('CLOSE_MODAL')
        this.cleanAndCloseForm()
      } catch (error) {
        if (this.isChangeFieldsRequiredError(error)) {
          this.changeFieldsModalError = this.$t('variation.changeFieldsRequiredError')
          this.$eventBus.$emit('CLOSE_MODAL')
          this.$nextTick(() => this.openChangeDetailsDialog())
        }
      }
    },
    async unlockVariation () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        return
      }
      try {
        const previousStatus = this.form.status
        const nextStatus = this.type === 'amendment' ? 'DRA' : 'ONG'
        this.form.status = nextStatus
        this.setAutoStatusChangeDetails(previousStatus, nextStatus)
        await this.saveForm()
        this.cleanAndCloseForm()
      } catch (error) {
        if (this.isApplicationExistsError(error)) {
          this.showApplicationExistsModal()
        } else if (this.isChangeFieldsRequiredError(error)) {
          this.changeFieldsModalError = this.$t('variation.changeFieldsRequiredError')
          this.openChangeDetailsDialog()
        }
      }
    },
    async handleCreateOrUpdateMilestone () {
      this.$v.form.$touch()
      this.checkForAgreedValueError()
      if (!this.$v.form.$invalid && !this.agreedValuesError.length) {
        await createMilestoneForVariation({ ...this.form, isGhost: undefined, currency: undefined, partsIds: undefined })

        this.cleanAndCloseForm()
        this.$emit('update')
      }
    },
    async handleChangeTo (newType) {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        const previousType = this.form.type
        const previousStatus = this.form.status
        const nextStatus = this.form.type === 'claim' && newType === 'variation' ? 'C2V' : 'ONG'
        this.form.status = nextStatus
        this.form.type = newType
        this.setAutoTypeChangeDetails({
          fromType: previousType,
          toType: newType,
          fromStatus: previousStatus,
          toStatus: nextStatus
        })
        try {
          await updateVariation({
            ...this.prepareUpdatePayload(),
            partsIds: undefined
          })
          this.cleanAndCloseForm()
          this.$emit('update')
        } catch (error) {
          if (this.isChangeFieldsRequiredError(error)) {
            this.changeFieldsModalError = this.$t('variation.changeFieldsRequiredError')
            this.openChangeDetailsDialog()
          }
        }
      }
    },
    async handleFinalizeVariation () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        return
      }
      this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
        scope: {
          message: this.$t('variation.finalizeConfirm'),
          subtitle: this.form.agreedValueCents
            ? this.$t('variation.finalizeConsequencesValue')
            : this.$t('variation.finalizeConsequencesNullValue')
        },
        handlers: {
          yes: async () => {
            const wasClaim =
              this.form.revisionHistory &&
              this.form.revisionHistory.find(
                (i) => i.comment === 'Claim changed to Variation'
              )
            const previousStatus = this.form.status
            const nextStatus = wasClaim ? 'C2V' : 'FIN'
            this.form.status = nextStatus
            this.setAutoStatusChangeDetails(previousStatus, nextStatus)
            try {
              await this.saveForm()
              this.cleanAndCloseForm()
            } catch (error) {
              if (this.isChangeFieldsRequiredError(error)) {
                this.changeFieldsModalError = this.$t('variation.changeFieldsRequiredError')
                this.openChangeDetailsDialog()
              }
            }
          }
        }
      })
    },
    showApplicationExistsModal () {
      this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
        scope: {
          message: this.$t('variation.applicationExists'),
          subtitle: this.$t('variation.applicationExistsExplanation')
        },
        handlers: {
          yes: () => {
            this.$eventBus.$emit('CLOSE_MODAL')
            this.$router.push({
              name: 'applications',
              params: { contractId: this.$route.params.contractId }
            })
          }
        }
      })
    },
    handleUnlockVariation () {
      if (this.dirty) {
        return
      }

      if (this.type === 'variation' || this.type === 'amendment') {
        this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
          scope: {
            message: this.$t('variation.unlockConfirm', { type: this.type }),
            subtitle: this.$t('variation.unlockConfirmConsequences', { type: this.type })
          },
          handlers: {
            yes: () => {
              this.unlockVariation()
            }
          }
        })
      } else {
        this.unlockVariation()
      }
    },
    calculateRatio (part, value, currency) {
      if (value.parts && value.parts.length === 1) {
        return 100
      }
      if (value.estimatedValueCents && value.estimatedValueCents.sum && part.variationPart && part.variationPart.estimatedValueCents) {
        return Math.round((part.variationPart.estimatedValueCents * currency.value) / value.estimatedValueCents.sum * 100)
      }
      if (value.originalValueCents && value.originalValueCents.sum && part.variationPart && part.variationPart.originalValueCents) {
        return Math.round((part.variationPart.originalValueCents * currency.value) / value.originalValueCents.sum * 100)
      }
      if (value.agreedValueCents && value.agreedValueCents.sum && part.variationPart && part.variationPart.agreedValueCents) {
        return Math.round((part.variationPart.agreedValueCents * currency.value) / value.agreedValueCents.sum * 100)
      }
      if (value.eotAgreed && value.eotAgreed.sum && part.variationPart && part.variationPart.eotAgreed) {
        return Math.round(part.variationPart.eotAgreed / value.eotAgreed.sum * 100)
      }
      if (value.eotRequested && value.eotRequested.sum && part.variationPart && part.variationPart.eotRequested) {
        return Math.round(part.variationPart.eotRequested / value.eotRequested.sum * 100)
      }
      return part.ratio
    },
    buildVariationForm (value) {
      const formValue = cloneDeep(value)
      delete formValue.changeTitle

      const parts = (value.parts || this.parts).map(part => {
        const fieldValueOrNull = (fieldName) => value.id ? part.variationPart[fieldName] || null : null
        return {
          partId: part.id,
          currency: part.currency,
          ratio: part.ratio,
          variationRatio: this.calculateRatio(part, value, this.currencies.find(i => i.key === part.currency)),
          name: part.name,
          estimatedValueCents: fieldValueOrNull('estimatedValueCents'),
          originalValueCents: fieldValueOrNull('originalValueCents'),
          agreedValueCents: fieldValueOrNull('agreedValueCents'),
          eotRequested: fieldValueOrNull('eotRequested'),
          eotAgreed: fieldValueOrNull('eotAgreed')
        }
      })

      return {
        ...formValue,
        estimatedValueCents: value.estimatedValueCents ? value.estimatedValueCents.sum : null,
        originalValueCents: value.originalValueCents ? value.originalValueCents.sum : null,
        agreedValueCents: value.agreedValueCents ? value.agreedValueCents.sum : null,
        changeDescription: '',
        paymentMilestone: undefined,
        reportingSide: this.reporter || value.reportingSide,
        type: value.type || this.type,
        risk: this.$store.state.risks.find(i => i.id === value.riskId),
        parts,
        partsIds: parts.map(i => i.partId)
      }
    },
    prepareVariation (value) {
      const status = this.$store.state.statuses.find((i) => i.code === value.status)
      this.isFinalised = status ? !status.editable.includes(this.type) : false
      return this.buildVariationForm(value)
    },
    async handleAddNewCategory () {
      const causeInArray = this.categoriesList.find(
        (i) => i.name.toUpperCase() === this.form.category.toUpperCase()
      )
      if (!causeInArray) {
        await saveCause({ name: this.form.category, area: this.area })
        await getAllDictionaries()
      }
    },
    errorMessages (key) {
      return errorMessageGenerator(this.$v.form[key], key)
    },
    async saveForm () {
      this.form.category = this.form.category.name || this.form.category
      this.form.riskId = this.form.risk ? this.form.risk.id : null
      this.form.type = this.form.type || this.type
      this.form.workstream = this.form.workstream ? this.form.workstream.name : null
      this.form.id
        ? await updateVariation(this.prepareUpdatePayload())
        : await createVariationForContract(this.form.contractId, {
          ...this.form,
          lastUpdateDate: undefined,
          partsIds: undefined,
          risk: undefined,
          isGhost: undefined,
          changeDescription: undefined
        })
      // this.form = this.prepareVariation(variation)
      this.$emit('update')
    },
    async handleSaveClick () {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        try {
          if (!this.hasAnyChanges) {
            this.$store.dispatch('createMessage', {
              text: this.$t('variation.noChangesToSave'),
              color: 'info'
            })
            return
          }

          if (this.shouldRequireChangeDetails) {
            this.changeFieldsModalError = null
            this.openChangeDetailsDialog()
            return
          }

          if (this.hasStatusChanged) {
            this.setAutoStatusChangeDetails(this.value.status, this.form.status)
          } else {
            this.clearChangeDetails()
          }

          await this.saveForm()
          this.cleanAndCloseForm()
        } catch (error) {
          if (this.isChangeFieldsRequiredError(error)) {
            this.changeFieldsModalError = this.$t('variation.changeFieldsRequiredError')
            this.openChangeDetailsDialog()
          }
        }
      }
    },
    makeUpgrade () {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        if (
          !this.agreedValuesError.length &&
          !this.originalValuesError.length
        ) {
          this.$emit('UPGRADE_VARIATION', this.form)
          this.cleanAndCloseForm()
        }
      }
    },
    handleUpgradeClick () {
      const subtitleDefault = this.$t('variation.stageChangeConsequences', { type: this.form.type })
      const subtitleVariationStageOne = this.form.agreedValueCents ? this.$t('variation.finalizeConsequencesValue') : this.$t('variation.finalizeConsequencesNullValue')
      const subtitle = `${subtitleDefault} ${this.stagesList.length === this.form.stage + 2 && this.form.type === 'variation' ? subtitleVariationStageOne : ''}`

      this.$eventBus.$emit('OPEN_CONFIRM_MODAL', {
        scope: {
          message: this.$t('variation.upgradeConfirm', {
            new: this.stagesList[this.form.stage + 1].name,
            type: this.form.type
          }),
          subtitle
        },
        handlers: {
          yes: () => {
            this.makeUpgrade()
          }
        }
      })
    },
    handleCancelClick () {
      this.cleanAndCloseForm()
    },
    cleanAndCloseForm () {
      this.form = {}
      this.form = this.prepareVariation(this.value)
      this.$v.$reset()
      this.$eventBus.$emit('CLOSE_MODAL')
      this.agreedValuesError = []
      this.originalValuesError = []
      this.changeFieldsModalError = null
    },
    handleOpenRiskDialog () {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'RiskForm',
        options: { maxWidth: 650 },
        scope: {
          value: {
            name: this.riskSearch
          }
        },
        handlers: {
          save: (risk) => {
            this.form.risk = risk
            this.riskSearch = risk.name
            this.$eventBus.$emit('CLOSE_MODAL')
          },
          cancel: () => {
            this.form.riskId = null
            this.$eventBus.$emit('CLOSE_MODAL')
          }
        }
      })
    },
    handleSetDirty () {
      this.dirty = true
    }
  },
  validations: variationModel
}
</script>
